import type { FileUpload } from 'graphql-upload/GraphQLUpload.js'
import GraphQLUpload from 'graphql-upload/GraphQLUpload.js'
import { ObjectType, Field, InputType, Int, ID, registerEnumType, Float } from 'type-graphql'

import { ObjectRef, ScalarObject } from '@things-factory/shell'

import { KpiValue, KpiValueInputType } from './kpi-value'

@InputType({ description: 'Input type for creating a new KPI value. Used in mutations to provide KPI value details.' })
export class NewKpiValue {
  @Field(type => ID, { description: 'ID of the KPI to which this value belongs.' })
  kpiId: string

  @Field(type => Int, { description: 'Version of the KPI definition at the time this value is calculated.' })
  version: number

  @Field({ description: 'Date or period for which this KPI value is recorded (e.g., day, month, quarter).' })
  valueDate: string

  @Field(type => Float, { description: 'The calculated numeric value for this KPI and period.' })
  value: number

  @Field(type => Float, {
    nullable: true,
    description: 'Performance score calculated from KPI value using scoreFormula or grades lookup table. Range: 0-1.'
  })
  score?: number

  @Field(type => ScalarObject, {
    nullable: true,
    description:
      'Extended or non-numeric information related to this KPI value, stored as JSON5. Can include grade, status, comments, or other metadata.'
  })
  meta?: any

  @Field(type => ID, { nullable: true, description: 'ID of the KPI organization scope for this value.' })
  kpiOrgScopeId?: string

  @Field({ nullable: true, description: 'Legacy organizational unit identifier (for backward compatibility)' })
  org?: string

  @Field({ nullable: true, description: 'Group key for this value (organization, line, user, project, etc.)' })
  group?: string

  @Field(type => KpiValueInputType, {
    nullable: true,
    description: 'Indicates whether this value was entered manually or generated automatically.'
  })
  inputType?: KpiValueInputType

  @Field({ nullable: true, description: 'Source of the value (e.g., system, user, external integration).' })
  source?: string
}

@InputType({
  description: 'Input type for updating an existing KPI value. Used in mutations to patch KPI value details.'
})
export class KpiValuePatch {
  @Field(type => ID, { nullable: true, description: 'ID of the KPI value to update.' })
  id?: string

  @Field(type => ID, { nullable: true, description: 'ID of the KPI to which this value belongs.' })
  kpiId?: string

  @Field(type => Int, {
    nullable: true,
    description: 'Version of the KPI definition at the time this value is calculated.'
  })
  version?: number

  @Field({
    nullable: true,
    description: 'Date or period for which this KPI value is recorded (e.g., day, month, quarter).'
  })
  valueDate?: string

  @Field(type => Float, { nullable: true, description: 'The calculated numeric value for this KPI and period.' })
  value?: number

  @Field(type => Float, {
    nullable: true,
    description: 'Performance score calculated from KPI value using scoreFormula or grades lookup table. Range: 0-1.'
  })
  score?: number

  @Field(type => ScalarObject, {
    nullable: true,
    description:
      'Extended or non-numeric information related to this KPI value, stored as JSON5. Can include grade, status, comments, or other metadata.'
  })
  meta?: any

  @Field(type => ID, { nullable: true, description: 'ID of the KPI organization scope for this value.' })
  kpiOrgScopeId?: string

  @Field({ nullable: true, description: 'Legacy organizational unit identifier (for backward compatibility)' })
  org?: string

  @Field({ nullable: true, description: 'Group key for this value (organization, line, user, project, etc.)' })
  group?: string

  @Field(type => KpiValueInputType, {
    nullable: true,
    description: 'Indicates whether this value was entered manually or generated automatically.'
  })
  inputType?: KpiValueInputType

  @Field({ nullable: true, description: 'Source of the value (e.g., system, user, external integration).' })
  source?: string

  @Field({ nullable: true, description: 'Custom flag for update operations (internal use).' })
  cuFlag?: string
}

@ObjectType()
export class KpiValueList {
  @Field(type => [KpiValue])
  items: KpiValue[]

  @Field(type => Int)
  total: number
}
